import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Editor, { loader } from '@monaco-editor/react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';

loader.config({ paths: { vs: `${process.env.PUBLIC_URL || ''}/monaco-vs` } });

const DEFAULT_START = new Date(new Date().setFullYear(new Date().getFullYear() - 1))
  .toISOString().slice(0, 10);
const DEFAULT_END = new Date().toISOString().slice(0, 10);

// ── Attribute catalog panel ───────────────────────────────────────────────────

function AttrParam({ param }) {
  return (
    <span className="text-[10px] font-mono">
      <span className="text-amber-400">{param.name}</span>
      {param.type !== 'any' && <span className="text-muted-foreground">:{param.type}</span>}
      {!param.required && param.default !== null && (
        <span className="text-muted-foreground">={String(param.default)}</span>
      )}
    </span>
  );
}

function AttrRow({ attr, onInsert }) {
  const [open, setOpen] = useState(false);
  const hasDictKeys = attr.dict_keys && attr.dict_keys.length > 0;

  return (
    <div className="border-b border-border/50 last:border-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-3 py-1.5 hover:bg-surface-raised/50 flex items-start gap-2 group"
      >
        <span className="text-[11px] font-mono text-foreground/90 shrink-0">{attr.alias}</span>
        <span className="text-[10px] text-muted-foreground leading-tight truncate flex-1">{attr.description}</span>
        <span className="text-[10px] text-muted-foreground/50 shrink-0">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="px-3 pb-2 flex flex-col gap-1.5">
          {/* Params */}
          {attr.params.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {attr.params.map((p) => <AttrParam key={p.name} param={p} />)}
            </div>
          )}
          {/* Return type */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground">returns</span>
            <span className="text-[10px] font-mono text-blue-400">{attr.return_type}</span>
            {hasDictKeys && (
              <span className="text-[10px] font-mono text-muted-foreground">
                [{attr.dict_keys.map((k) => `'${k}'`).join(', ')}]
              </span>
            )}
          </div>
          {/* Snippet + insert */}
          <div className="flex items-center gap-1.5 bg-surface-raised rounded px-2 py-1">
            <code className="text-[10px] font-mono text-foreground/80 flex-1 truncate">
              {attr.snippet}
            </code>
            <button
              onClick={() => onInsert(attr.snippet)}
              className="text-[10px] text-primary hover:text-primary/80 shrink-0"
              title="Insert at cursor"
            >
              Insert
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AttributesPanel({ editorRef }) {
  const [catalog, setCatalog] = useState([]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get('/screeners/attributes').then((r) => {
      setCatalog(r.data);
      // Expand first category by default
      if (r.data.length > 0) setExpanded({ [r.data[0].name]: true });
    });
  }, []);

  const handleDownload = () => {
    const url = api.defaults.baseURL
      ? api.defaults.baseURL.replace(/\/$/, '') + '/screeners/attributes/doc'
      : '/api/screeners/attributes/doc';
    const a = document.createElement('a');
    a.href = url;
    a.download = 'screener_reference.md';
    a.click();
  };

  const q = search.toLowerCase();
  const filtered = catalog.map((cat) => ({
    ...cat,
    attributes: cat.attributes.filter(
      (a) => !q || a.alias.toLowerCase().includes(q) || a.description.toLowerCase().includes(q)
    ),
  })).filter((cat) => cat.attributes.length > 0);

  const handleInsert = (snippet) => {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = editor.getSelection();
    editor.executeEdits('attr-insert', [{
      range: selection,
      text: snippet,
      forceMoveMarkers: true,
    }]);
    editor.focus();
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-2 border-b border-border flex gap-2">
        <Input
          placeholder="Search attributes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="text-xs h-7 flex-1"
        />
        <button
          onClick={handleDownload}
          title="Download LLM reference (.md)"
          className="text-[11px] text-muted-foreground hover:text-foreground border border-border rounded px-2 h-7 shrink-0"
        >
          ↓ ref
        </button>
        {/* A new tab, so unsaved code is still here on the way back. */}
        <Link
          to="/university"
          target="_blank"
          rel="noopener noreferrer"
          title="Learn what each attribute means"
          className="inline-flex items-center text-[11px] text-muted-foreground hover:text-foreground border border-border rounded px-2 h-7 shrink-0"
        >
          Learn
        </Link>
      </div>
      <div className="flex-1 overflow-y-auto text-xs">
        {filtered.length === 0 && (
          <p className="text-muted-foreground p-3 text-[11px]">No attributes match.</p>
        )}
        {filtered.map((cat) => (
          <div key={cat.name}>
            <button
              onClick={() => setExpanded((e) => ({ ...e, [cat.name]: !e[cat.name] }))}
              className="w-full text-left px-3 py-1.5 bg-surface-raised/30 hover:bg-surface-raised/60 flex justify-between items-center"
            >
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {cat.name}
              </span>
              <span className="text-[10px] text-muted-foreground/50">
                {expanded[cat.name] ? '▲' : '▼'}
              </span>
            </button>
            {expanded[cat.name] && (
              <div>
                {cat.attributes.map((attr) => (
                  <AttrRow key={attr.alias} attr={attr} onInsert={handleInsert} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RunOutput(result) {
  return <div className='rounded'>{result.output && result.output.map((row)=><Card className="p-1 mt-1">
    <CardContent>
      <div className='flex items-center justify-between w-full pt-2'>
        <p>{row.date}</p>
        <h2 className="text-sm font-bold text-foreground tracking-tight">{row.symbol.replace('.NS', '')}</h2>
      </div>
      <div className='text-xs text-muted-foreground font-mono pt-2'>
        {row.metadata && Object.keys(row.metadata).length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {row.score && <span className="bg-surface-base px-2 py-0.5 rounded border border-border/50 whitespace-nowrap">
              <span className="opacity-70 mr-1">score:</span>
              <span className="text-foreground">{row.score.toFixed(2)}</span>
            </span>}
            {Object.entries(row.metadata).map(([k, v]) => (
              <span key={k} className="bg-surface-base px-2 py-0.5 rounded border border-border/50 whitespace-nowrap">
                <span className="opacity-70 mr-1">{k}:</span>
                <span className="text-foreground">{typeof v === 'number' ? v.toFixed(2) : v}</span>
              </span>
            ))}
          </div>
        )
        : <span className="opacity-40 italic">No metadata</span>}
      </div>
    </CardContent>
  </Card>)}</div>
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Builder() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { user } = useAuth();

  const [screener, setScreener] = useState(null);
  const [code, setCode] = useState('');
  const [saved, setSaved] = useState(true);
  const [saving, setSaving] = useState(false);

  // Someone who may not author Python can still read it — including the code
  // generated from their own spec — but not save it.
  const isReadOnly = screener !== null
    && (screener.is_owner === false || !user?.can_author_code);

  const [runConfig, setRunConfig] = useState({ start: DEFAULT_START, end: DEFAULT_END });
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState(null);
  const [rightTab, setRightTab] = useState('output'); // 'output' | 'attrs'

  const editorRef = useRef(null);

  useEffect(() => {
    api.get(`/screeners`).then((r) => {
      const s = r.data.find((x) => x.id === parseInt(id));
      setScreener(s || null);
      // A spec screener's Python is generated; it is edited as a spec.
      if (s && s.kind === 'spec') navigate(`/spec/${id}`, { replace: true });
    });
    api.get(`/screeners/${id}/file`).then((r) => {
      setCode(r.data.content);
      setSaved(true);
    });
  }, [id, navigate]);

  const handleEditorChange = (value) => {
    setCode(value);
    setSaved(false);
  };

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await api.put(`/screeners/${id}/file`, { content: code });
      setSaved(true);
    } finally {
      setSaving(false);
    }
  }, [id, code]);

  const handleRun = async () => {
    if (!saved && !isReadOnly) await handleSave();
    setRunning(true);
    setOutput(null);
    setRightTab('output');
    try {
      const res = await api.post(`/screeners/${id}/run`, runConfig);
      setOutput(res.data);
    } catch (err) {
      setOutput({ error: err.response?.data?.error || 'Run failed' });
    } finally {
      setRunning(false);
    }
  };

  // Ctrl+S / Cmd+S to save
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (!isReadOnly) handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleSave, isReadOnly]);

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <header className="flex-none flex items-center gap-3 h-12 px-4 bg-surface-base border-b border-border">
        <Button variant="ghost" size="sm" onClick={() => navigate('/screeners')} className="px-2">
          <span className="sm:hidden">←</span>
          <span className="hidden sm:inline">← Screeners</span>
        </Button>
        <span className="font-medium text-sm text-foreground truncate max-w-[120px] sm:max-w-[200px]">{screener?.name || '…'}</span>
        <div className="flex items-center gap-1.5 sm:gap-2 ml-auto w-full md:w-auto mt-2 md:mt-0 justify-end">
          {isReadOnly ? (
            <span className="text-[10px] sm:text-xs mr-1 sm:mr-2 text-muted-foreground border border-border rounded px-1.5 py-0.5">
              Read-only
            </span>
          ) : (
            <span className={`text-[10px] sm:text-xs mr-1 sm:mr-2 ${saved ? 'text-win' : 'text-amber-400'}`}>
              {saved ? 'Saved' : 'Unsaved changes'}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate(`/signals/${id}`)} className="text-xs px-2 h-8">
            Signals
          </Button>
          {!isReadOnly && (
            <Button variant="outline" size="sm" onClick={handleSave} disabled={saving || saved} className="text-xs px-2 h-8">
              {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Saving…</> : 'Save'}
            </Button>
          )}
          <Button size="sm" onClick={handleRun} disabled={running} className="text-xs px-2 h-8">
            {running ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Running…</> : '▶ Run'}
          </Button>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden">
        {/* Editor pane */}
        <div className="flex-1 min-h-[400px] md:min-h-0 border-b md:border-b-0 border-border md:border-transparent">
          <Editor
            height="100%"
            defaultLanguage="python"
            theme="vs-dark"
            value={code}
            onChange={handleEditorChange}
            onMount={(editor) => { editorRef.current = editor; }}
            options={{
              fontSize: 13,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              tabSize: 4,
              wordWrap: 'on',
              readOnly: isReadOnly,
            }}
          />
        </div>

        {/* Right pane */}
        <div className="w-full max-w-[400px] md:w-100 flex-none flex flex-col md:border-l border-border bg-surface-base md:overflow-hidden">
          {/* Tab bar */}
          <div className="flex-none flex border-b border-border">
            {['output', 'attrs'].map((tab) => (
              <button
                key={tab}
                onClick={() => setRightTab(tab)}
                className={`flex-1 py-1.5 text-[11px] font-medium transition-colors ${
                  rightTab === tab
                    ? 'text-foreground border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab === 'output' ? 'Output' : 'Attributes'}
              </button>
            ))}
          </div>

          {/* Output tab */}
          {rightTab === 'output' && (
            <div className="flex-1 flex flex-col overflow-y-auto">
              {/* Run config */}
              <div className="p-4 border-b border-border flex flex-col gap-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Run config</h3>
                <div className="flex flex-row md:flex-col gap-3 md:gap-1">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-muted-foreground">Start date</label>
                    <Input type="date" value={runConfig.start}
                      onChange={(e) => setRunConfig({ ...runConfig, start: e.target.value })} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-muted-foreground">End date</label>
                    <Input type="date" value={runConfig.end}
                      onChange={(e) => setRunConfig({ ...runConfig, end: e.target.value })} />
                  </div>
                </div>
              </div>
              {/* Output */}
              <div className="p-4 flex flex-col gap-2 flex-1 min-h-[200px] md:min-h-0">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                  Output
                  {output && (
                    <span className={`text-xs font-mono px-1.5 py-0.5 rounded ml-1 ${
                      output.exit_code === 0
                        ? 'bg-win/10 text-win'
                        : 'bg-destructive/10 text-destructive'
                    }`}>
                      {output.error ? 'error' : output.exit_code === 0 ? 'ok' : `exit ${output.exit_code}`}
                    </span>
                  )}
                </h3>
                {running && <p className="text-xs text-muted-foreground">Running…</p>}
                <div className="flex flex-col gap-2 overflow-x-auto no-scrollbar">
                  {output?.error && (
                    <pre className="text-xs font-mono whitespace-pre-wrap wrap-break-word text-destructive bg-destructive/5 rounded p-2">{output.error}</pre>
                  )}
                  {output?.stderr && (
                    <pre className="text-xs font-mono whitespace-pre-wrap wrap-break-word text-destructive bg-destructive/5 rounded p-2">{output.stderr}</pre>
                  )}
                  {output?.stdout && (
                    <pre className="text-xs font-mono whitespace-pre-wrap wrap-break-word text-win bg-win/5 rounded p-2"><RunOutput output={JSON.parse(output.stdout)} /></pre>
                  )}
                  {!running && !output && (
                    <p className="text-xs text-muted-foreground">Press ▶ Run to execute</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Attributes tab */}
          {rightTab === 'attrs' && (
            <div className="flex-1 overflow-hidden">
              <AttributesPanel editorRef={editorRef} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
